import React, { useState, useEffect, useRef } from "react";
import { keyframes } from "@mui/system";
import { Box, Typography } from "../../../design-system";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import DemoVideoPicker from "./DemoVideoPicker";
import { getDemoShareSrc, getDemoSelectionId, SAMPLES, DEMO_SRC_EVENT } from "./demoSource";
import logoSite from "../../../../assets/brand/logo_site.svg";

/**
 * FlagshipLiveEt — a self-contained, reusable "live ET" hero component.
 *
 * This is an isolated COPY of the module-private LiveSpreadsheet pattern from SplitHeroEtPage.tsx
 * (the "Letter O" hero). It renders the real ET editor live in an iframe (via the shared /s/ share
 * URL from ./demoSource), with a Desktop/Phone toggle, zoom −/+ controls, a full-screen overlay,
 * and the embedded <DemoVideoPicker/>. It is intentionally decoupled from any surrounding hero
 * layout so it can be dropped in anywhere.
 *
 * Nothing here is imported by or shared back into the original hero pages — this file exists so the
 * live-ET surface can be evolved without touching SplitHeroEtPage.tsx / HeroEtPage.tsx.
 */

const font = "Outfit, sans-serif";

// Gentle right-nudge for the "scroll →" affordance on the desktop ET (the sheet is wider than the frame).
const scrollNudge = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(5px); }
`;

// Phone preview: render the ET at a true phone viewport (below its 500px xs breakpoint) so it
// shows the genuine mobile layout, then scale it into a phone-sized frame (mirrors SplitHeroEtPage).
// 496 (just under the 500px breakpoint) MAXIMISES the mobile-layout width, so the transcript column
// gets its full room and stops clipping at the frame's right edge (the frame can't be swiped like a
// real phone, so anything past the viewport is lost — we give the sheet as much viewport as the
// mobile layout allows). Frame is a true phone shape (300×600).
const PHONE_VIEWPORT_W = 496;
const PHONE_FRAME_W = 300;
const PHONE_FRAME_H = 600;
const PHONE_SCALE = PHONE_FRAME_W / PHONE_VIEWPORT_W;
const DESKTOP_ZOOM_DEFAULT = 0.55; // shrink the full-width editor so every spreadsheet column shows
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
// Zoom < 1 renders the iframe LARGER (100/scale %) then scales it down — so the full-width editor
// gets more horizontal room and its left checkboxes + right timecode steppers all fit instead of
// clipping at the box edges.
const iframeStyle = (scale: number): React.CSSProperties => ({
  position: "absolute", top: 0, left: 0, width: `${100 / scale}%`, height: `${100 / scale}%`,
  transform: `scale(${scale})`, transformOrigin: "top left", border: "none", background: "#0b0a0f",
});
const zoomBtn = { cursor: "pointer", background: "transparent", border: "none", color: "#c2c7d6", fontFamily: font, fontSize: "14px", lineHeight: 1, px: "8px", py: "3px", "&:hover": { color: "#fff", background: "rgba(255,255,255,0.08)" } };

const DEFAULT_TITLE = "Film Reel — live";
const DEFAULT_HEIGHT = 556;

// Title-bar label that always names the ACTUAL demo showing (the selection is persisted in
// demoSource), so it can never say "Film Reel — live" while a different demo is loaded.
const demoTitleFor = (fallback: string): string => {
  try {
    const id = getDemoSelectionId(); // "film" | "s:<sampleId>" | "<transcriptId>"
    if (id === "film") return "Film Reel — live";
    const sid = id.startsWith("s:") ? id.slice(2) : id;
    const s = SAMPLES.find((x) => x.id === sid);
    if (s) return `${s.title.replace(/^🍒\s*/, "").replace(/\s*\(sample\)\s*$/, "")} — live`;
    return "Your video — live"; // one of the logged-in user's own library videos
  } catch (e) {
    return fallback;
  }
};

export interface FlagshipLiveEtProps {
  /** Label shown in the window title bar. Defaults to "Film Reel — live". */
  title?: string;
  /** Which view to open in. Defaults to "desktop". */
  defaultMode?: "desktop" | "phone";
  /** Height (px) of the desktop editor viewport. Defaults to 556. */
  heightPx?: number;
  /** Phone-only, no window chrome/toggle/picker — just the framed mobile ET (for custom carousels). */
  bare?: boolean;
  /** Initial desktop zoom (bigger = larger thumbnails/text, fewer columns). Defaults to 0.55. */
  defaultZoom?: number;
  /** Bare phone only: show external Phone/Full · Compact/Simple/Media · zoom controls under the frame. */
  controls?: boolean;
}

/** The real ET editor, embedded live. Desktop = scaled full editor; Phone = framed mobile view. */
const FlagshipLiveEt: React.FC<FlagshipLiveEtProps> = ({
  title = DEFAULT_TITLE,
  defaultMode = "desktop",
  heightPx = DEFAULT_HEIGHT,
  bare = false,
  defaultZoom = DESKTOP_ZOOM_DEFAULT,
  controls = false,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState<"desktop" | "phone">(defaultMode);
  const [shareSrc, setShareSrc] = useState<string>(getDemoShareSrc());
  const [demoTitle, setDemoTitle] = useState<string>(() => demoTitleFor(title));
  const [zoom, setZoom] = useState(defaultZoom);
  const [fs, setFs] = useState(false);
  const [showHint, setShowHint] = useState(true);
  // bare-phone external controls (drive the real ET via its existing view/etmode/zoom bridge)
  const bareFrameRef = useRef<HTMLIFrameElement | null>(null);
  const [pMode, setPMode] = useState(0); // ET_MODES: 0 Compact · 1 Simple · 2 Media
  const [pZoom, setPZoom] = useState(1);
  const src = inView ? shareSrc : "";
  const sendPhoneCmd = (c: Record<string, unknown>) => {
    const w = bareFrameRef.current?.contentWindow;
    if (!w) return;
    let target = window.location.origin;
    try { if (/^https?:\/\//.test(shareSrc)) target = new URL(shareSrc).origin; } catch (e) { /* ignore */ }
    w.postMessage({ type: "cc-et-cmd", ...c }, target);
  };
  // The ET remembers a single global view mode (cc_et_view: auto/phone/full) across ALL same-origin
  // embeds — so a "full" left over from the Premiere panel or a prior toggle would cram the wide
  // spreadsheet into this narrow phone frame. Force "auto" on load: this 496px iframe then falls back
  // to viewport detection → the mobile layout. ("auto" is safe for the wide desktop embed too, which
  // resolves auto → full; forcing "phone" would leak through the shared key and break that embed.)
  const forceAutoView = () => {
    sendPhoneCmd({ cmd: "view", mode: "auto" });
    sendPhoneCmd({ cmd: "etmode", index: pMode });
  };

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      (es) => { if (es.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); } },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  // The demo-video picker (shared with the hero pages) announces a new /s/ src here.
  useEffect(() => {
    const onSrc = (e: Event) => {
      const next = (e as CustomEvent).detail;
      if (typeof next === "string" && next) { setShareSrc(next); setLoaded(false); setDemoTitle(demoTitleFor(title)); }
    };
    window.addEventListener(DEMO_SRC_EVENT, onSrc);
    return () => window.removeEventListener(DEMO_SRC_EVENT, onSrc);
  }, []);

  // Scroll affordance: at this zoom the desktop sheet is wider than the frame, so hint that there
  // are more columns to the right. Auto-fades after a beat, and re-arms when the source/mode changes.
  useEffect(() => {
    if (mode !== "desktop") { setShowHint(false); return; }
    setShowHint(true);
    const t = window.setTimeout(() => setShowHint(false), 6500);
    return () => window.clearTimeout(t);
  }, [src, mode]);

  const switchMode = (m: "desktop" | "phone") => { setMode(m); setLoaded(false); };

  const toggleBtn = (m: "desktop" | "phone", label: string, Icon: React.ElementType) => {
    const active = mode === m;
    return (
      <Box
        component="button"
        onClick={() => switchMode(m)}
        sx={{ display: "inline-flex", alignItems: "center", gap: "6px", cursor: "pointer", background: active ? "linear-gradient(100deg,#e61d50,#b3123c)" : "transparent", color: active ? "#fff" : "#c2c7d6", border: "none", fontFamily: font, fontWeight: 700, fontSize: "12.5px", px: "13px", py: "7px" }}
      >
        <Icon sx={{ fontSize: 15 }} /> {label}
      </Box>
    );
  };

  const loadingOverlay = (
    <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", background: "#0b0a0f" }}>
      <img src={logoSite} alt="" width={36} style={{ opacity: 0.85 }} />
      <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#9aa0b4" }}>Loading the live editor…</Typography>
    </Box>
  );

  // Bare mode: the framed mobile ET, optionally with external Phone/Full · mode · zoom controls.
  if (bare) {
    const ctrlBtn = (active: boolean, onClick: () => void, label: string) => (
      <Box
        component="button"
        onClick={onClick}
        sx={{ cursor: "pointer", fontFamily: font, fontSize: "11px", fontWeight: 700, px: "9px", py: "5px", borderRadius: "7px", lineHeight: 1, border: "1px solid", borderColor: active ? "transparent" : "rgba(255,255,255,0.16)", background: active ? "linear-gradient(100deg,#e61d50,#b3123c)" : "transparent", color: active ? "#fff" : "#c2c7d6", "&:hover": { color: "#fff", borderColor: active ? "transparent" : "rgba(255,255,255,0.3)" } }}
      >
        {label}
      </Box>
    );
    const bumpZoom = (d: number) => {
      const f = clamp(Math.round((pZoom + d) * 10) / 10, 0.8, 1.2);
      setPZoom(f);
      sendPhoneCmd({ cmd: "zoom", factor: f });
    };
    return (
      <Box ref={ref} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
        <Box sx={{ position: "relative", boxSizing: "content-box", width: PHONE_FRAME_W, height: PHONE_FRAME_H, borderRadius: "34px", border: "10px solid #15131b", background: "#0b0a0f", overflow: "hidden", boxShadow: "0 30px 80px -40px rgba(0,0,0,0.9)" }}>
          {src ? (
            <iframe
              ref={bareFrameRef}
              title="CherryClipz — mobile view"
              src={src}
              allow="fullscreen; clipboard-write"
              onLoad={() => {
                // Force the mobile layout (clearing any sticky "full"), retrying until the ET's
                // message listener is mounted; keep the overlay up until it lands so a stale
                // "full" state never flashes the desktop grid inside the phone.
                forceAutoView();
                window.setTimeout(forceAutoView, 300);
                window.setTimeout(() => { forceAutoView(); setLoaded(true); }, 700);
              }}
              style={{ position: "absolute", top: 0, left: 0, border: "none", width: PHONE_VIEWPORT_W, height: Math.round(PHONE_FRAME_H / PHONE_SCALE), transform: `scale(${PHONE_SCALE})`, transformOrigin: "top left" }}
            />
          ) : null}
          {!loaded && loadingOverlay}
        </Box>
        {controls && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "center", background: "rgba(17,16,23,0.94)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "14px", px: "14px", py: "12px", boxShadow: "0 14px 34px -18px rgba(0,0,0,0.6)" }}>
            <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 600, color: "#cbd0dc" }}>
              👇 See it your way — pick a view mode:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap", justifyContent: "center" }}>
              {["Compact", "Simple", "Media"].map((m, i) => ctrlBtn(pMode === i, () => { setPMode(i); sendPhoneCmd({ cmd: "etmode", index: i }); }, m))}
              <Box sx={{ width: "1px", alignSelf: "stretch", background: "rgba(255,255,255,0.15)", mx: "3px" }} />
              {ctrlBtn(false, () => bumpZoom(-0.1), "−")}
              <Typography sx={{ fontFamily: font, fontSize: "10px", color: "#8d94a8", minWidth: 30, textAlign: "center", alignSelf: "center" }}>{Math.round(pZoom * 100)}%</Typography>
              {ctrlBtn(false, () => bumpZoom(0.1), "+")}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box ref={ref} sx={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
      <Box sx={{ borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 40px 90px -55px rgba(0,0,0,0.9), 0 0 0 1px rgba(230,29,80,0.10)", background: "#0b0a0f" }}>
        {/* window title bar — reads like an app, with a live dot + the Desktop/Phone toggle */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "13px", py: "8px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
          <Box sx={{ display: "flex", gap: "6px" }}>
            {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
              <Box key={c} sx={{ width: 11, height: 11, borderRadius: "50%", background: c, opacity: 0.55 }} />
            ))}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <Box aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", background: "#e61d50", boxShadow: "0 0 0 3px rgba(230,29,80,0.18)" }} />
            <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "12.5px", color: "#f3f4f9", display: { xs: "none", sm: "block" } }}>
              {demoTitle}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          {mode === "desktop" && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: "2px", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", overflow: "hidden" }}>
                <Box component="button" onClick={() => setZoom((z) => clamp(z - 0.1, 0.35, 1.1))} title="Zoom out" sx={zoomBtn}>−</Box>
                <Typography sx={{ fontFamily: font, fontSize: "11px", color: "#c2c7d6", px: "5px", minWidth: 34, textAlign: "center" }}>{Math.round(zoom * 100)}%</Typography>
                <Box component="button" onClick={() => setZoom((z) => clamp(z + 0.1, 0.35, 1.1))} title="Zoom in" sx={zoomBtn}>+</Box>
              </Box>
              <Box component="button" onClick={() => setFs(true)} title="Open full screen" sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", color: "#c2c7d6", fontFamily: font, fontSize: "11px", px: "8px", py: "4px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.3)" } }}>
                <OpenInFullIcon sx={{ fontSize: 13 }} /> Full screen
              </Box>
            </>
          )}
          <Box sx={{ display: "inline-flex", border: "1px solid #3a2b46", borderRadius: 999, overflow: "hidden" }}>
            {toggleBtn("desktop", "Desktop", DesktopWindowsIcon)}
            {toggleBtn("phone", "Phone", PhoneIphoneIcon)}
          </Box>
        </Box>

        {mode === "desktop" ? (
          <Box sx={{ position: "relative", height: `${heightPx}px` }}>
            {src ? (
              <iframe
                title="CherryClipz — live spreadsheet"
                src={src}
                allow="fullscreen; clipboard-write"
                onLoad={() => setLoaded(true)}
                style={iframeStyle(zoom)}
              />
            ) : null}
            {!loaded && loadingOverlay}
            {/* scroll → affordance: pointer-events:none so clicks pass through to the live ET */}
            {loaded && (
              <Box
                aria-hidden
                sx={{
                  position: "absolute", right: 0, top: 0, bottom: 0, width: "96px",
                  pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "flex-end", pr: "10px",
                  background: "linear-gradient(90deg, rgba(11,10,15,0) 0%, rgba(11,10,15,0.5) 80%)",
                  opacity: showHint ? 1 : 0, transition: "opacity .45s ease",
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: "5px", background: "rgba(230,29,80,0.94)",
                    color: "#fff", fontFamily: font, fontWeight: 700, fontSize: "11px", letterSpacing: "0.02em",
                    px: "9px", py: "5px", borderRadius: "999px", whiteSpace: "nowrap",
                    boxShadow: "0 8px 20px -8px rgba(0,0,0,0.8)", animation: `${scrollNudge} 1.4s ease-in-out infinite`,
                  }}
                >
                  scroll <Box component="span" sx={{ fontSize: "13px", lineHeight: 1 }}>→</Box>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", py: "22px", height: `${heightPx}px`, alignItems: "center" }}>
            <Box sx={{ position: "relative", boxSizing: "content-box", width: PHONE_FRAME_W, height: PHONE_FRAME_H, borderRadius: "34px", border: "10px solid #15131b", background: "#0b0a0f", overflow: "hidden", boxShadow: "0 30px 80px -40px rgba(0,0,0,0.9)" }}>
              {src ? (
                <iframe
                  title="CherryClipz — mobile view"
                  src={src}
                  allow="fullscreen; clipboard-write"
                  onLoad={() => setLoaded(true)}
                  style={{ position: "absolute", top: 0, left: 0, border: "none", width: PHONE_VIEWPORT_W, height: Math.round(PHONE_FRAME_H / PHONE_SCALE), transform: `scale(${PHONE_SCALE})`, transformOrigin: "top left" }}
                />
              ) : null}
              {!loaded && loadingOverlay}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
        <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#8d94a8" }}>
          <Box component="span" sx={{ color: "#e61d50" }}>↑</Box> Real, not a screenshot — click any clip to play it.
        </Typography>
        <DemoVideoPicker />
      </Box>

      {/* full-screen — scale to fit so nothing on the ET's edges is cut off */}
      {fs && (
        <Box onClick={() => setFs(false)} sx={{ position: "fixed", inset: 0, zIndex: 13000, background: "rgba(4,3,7,0.72)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", p: { xs: "8px", md: "18px" } }}>
          <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{ width: "100%", maxWidth: "1500px", height: "92vh", display: "flex", flexDirection: "column", background: "#0b0a0f", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "14px", overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", px: "12px", py: "8px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
              <Box component="button" onClick={() => setFs(false)} title="Close" sx={{ cursor: "pointer", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", color: "#c2c7d6", fontFamily: font, fontSize: "12px", px: "10px", py: "5px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.3)" } }}>Close ×</Box>
            </Box>
            <Box sx={{ position: "relative", flex: 1, overflow: "hidden" }}>
              {src ? <iframe title="CherryClipz — full editor" src={src} allow="fullscreen; clipboard-write" style={iframeStyle(0.82)} /> : null}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FlagshipLiveEt;
